import { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown } from 'lucide-react';

// Country name/key -> ISO 3166-1 alpha-2 code used by flag-icons
const CODE = {
  argentina: 'ar',
  brazil: 'br',
  chile: 'cl',
  colombia: 'co',
  mexico: 'mx',
  uruguay: 'uy',
};

// Renders a real SVG flag (flag-icons) so it displays on every OS, including
// Windows where the Unicode flag emoji do not render.
export const Flag = ({ country, size = 16, className = '' }) => {
  const key = (country || '').toLowerCase().trim();
  if (key === 'latam' || key === 'all') {
    return <Globe size={size} className={`inline-block align-middle text-blue-500 ${className}`} />;
  }
  const code = CODE[key];
  if (!code) return null;
  return (
    <span
      className={`fi fi-${code} align-middle ${className}`}
      style={{ fontSize: size, borderRadius: 2, lineHeight: 1 }}
    />
  );
};

// Custom country dropdown — native <option> elements cannot render images,
// so this replaces the native <select> when we want flags inside the menu.
// options: [{ value, label, country, group? }]  (country = key passed to <Flag>)
// When any option has a `group`, group headers are rendered in the menu.
export const CountrySelect = ({ value, onChange, options, className = '' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const selected = options.find(o => o.value === value) || options[0];
  const hasGroups = options.some(o => o.group);

  const renderOption = (o) => (
    <button
      key={o.value}
      type="button"
      onClick={() => { onChange(o.value); setOpen(false); }}
      className={`flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-slate-50 ${o.value === value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700'}`}
    >
      <Flag country={o.country} size={16} />
      <span>{o.label}</span>
    </button>
  );

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 border border-slate-300 rounded-lg px-3 py-2 text-sm font-medium bg-white hover:bg-slate-50 min-w-[10rem]"
      >
        {selected && <Flag country={selected.country} size={16} />}
        <span className="flex-1 text-left">{selected?.label}</span>
        <ChevronDown className="w-4 h-4 text-slate-400" />
      </button>
      {open && (
        <div className="absolute left-0 top-11 z-50 bg-white border border-slate-200 rounded-lg shadow-xl min-w-full max-w-[90vw] py-1 max-h-72 overflow-auto">
          {hasGroups
            ? [...new Set(options.map(o => o.group))].map(g => (
                <div key={g || '_'}>
                  {g && <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{g}</div>}
                  {options.filter(o => o.group === g).map(renderOption)}
                </div>
              ))
            : options.map(renderOption)}
        </div>
      )}
    </div>
  );
};
